import { RichTextFieldStrapiProps } from '@/components/atoms/RichTextField/RichTextFieldStrapi';

import { PageData } from '@/app/types';
import { TopBannerStrapiProps } from '@/blocks/topBanner/types';
import { APIPath } from '@/cms';
import { BlockHeader } from '@/cms/BlockHeaderContentDC';
import { UNDERSCORE_CHAR_REPLACEMENT } from '@/features/blog/SideNavigation/consts';
import { getRichTextFieldHeadingLevel2 } from '@/features/blog/SideNavigation/utils';
import { Breakpoints } from '@/utils/breakpoints';

import { BLOG_PATH_PREFIX, BLOG_TOPIC_PATH_PREFIX } from './api/constants';
import { Block } from './types';
import { Entry } from './types/entry';

export function extractValidValuesFromObjBreakpoints(
  obj: Record<string, unknown>,
  breakpoint: Breakpoints
) {
  const extractedProps: Record<string, unknown> = {};

  Object.entries(obj).forEach(([key, value]) => {
    if (key.startsWith(breakpoint)) {
      if (!value) return;

      const newKey = key.replace(breakpoint, '').toLowerCase();
      extractedProps[newKey] = value;
    }
  });

  return extractedProps;
}

export function aggregatePageData(entry?: Entry): PageData {
  return {
    theme: entry?.theme?.themeKey,
  };
}

export const slugify = (title?: string) => {
  if (!title) return '';

  const initialNumberingRegex = /^([0-9]\.\s?)+/g;
  const spacesRegex = /\/|\s+/g;
  const punctuationRegex = /[^a-zA-Z0-9\u4e00-\u9fa5-]+/g;

  return title
    .toLowerCase()
    .trim()
    .replaceAll(initialNumberingRegex, '')
    .replaceAll(spacesRegex, '-')
    .replaceAll('&', 'and')
    .replaceAll(punctuationRegex, '')
    .replaceAll('_', UNDERSCORE_CHAR_REPLACEMENT);
};

/**
 * Handle odd cases like dual slash and trailing slash slugs.
 * Ideally this should be handled by the CMS, but it's proven to be too complex.
 */
export const sanitiseSlug = (slug: string) => {
  if (!slug) return '';
  let result = slug.trim();

  result = result.replace(/\/{2,}/g, '/');

  if (result.startsWith('/')) {
    result = result.slice(1);
  }

  if (result.endsWith('/') && result.length > 1) {
    result = result.slice(0, -1);
  }

  return result;
};
export type SectionFit = 'full' | 'contained';
/**
@description maps to TW's layer-base custom max-width values in globals.css
 */
export function getWrapperMaxWidth(fit: SectionFit): string {
  if (fit === 'full') {
    return 'max-w-section-full';
  }

  return 'max-w-section-contained';
}

export function flattenEntry<T extends { id: string; attributes: Entry }>(
  entry: T
): { id: string } & T['attributes'] {
  return {
    id: entry.id,
    ...entry.attributes,
  };
}

export function getHeaderTitles(contentBlocks: Entry['blocks']) {
  // TODO: pull out into a transformer in utils
  const headerTitles: string[] = contentBlocks.reduce((acc, block) => {
    if (!!block?.text) {
      acc.push(
        ...getRichTextFieldHeadingLevel2(block.text as RichTextFieldStrapiProps)
      );
    }
    if (!!block?.text2) {
      acc.push(
        ...getRichTextFieldHeadingLevel2({
          text2: block.text2,
        } as RichTextFieldStrapiProps)
      );
    }
    if (!!(block?.header as BlockHeader)?.title) {
      const title = (block.header as BlockHeader).title;
      if (typeof title === 'string') {
        acc.push(title);
      }
    }
    return acc;
  }, [] as string[]);

  return headerTitles;
}

export function getStrapiApiPathForSlug(pathname: string): APIPath {
  const slug = sanitiseSlug(pathname);

  if (slug === BLOG_PATH_PREFIX) {
    return '/pages';
  }

  if (slug.startsWith(BLOG_TOPIC_PATH_PREFIX)) {
    return '/blog-topics';
  }

  if (slug.startsWith(`${BLOG_PATH_PREFIX}/page`)) {
    return '/pages';
  }

  if (slug.startsWith(`${BLOG_PATH_PREFIX}/authors`)) {
    return '/pages';
  }

  if (slug.startsWith(`${BLOG_PATH_PREFIX}/`)) {
    return '/blogs';
  }

  return '/pages';
}

export const TOP_BANNER_COMPONENT = 'shared.top-banner-component';
export const BANNER_DISMISS_KEY = 'topBannerDismissed';

export function detectBannerOffset(
  entry: Entry
): { mobile: number; desktop: number } | null {
  const block = entry.blocks?.find(
    (b) => b.__component === TOP_BANNER_COMPONENT
  ) as (Block & Partial<TopBannerStrapiProps> & { bannerContent?: { endDate?: string }[] }) | undefined;

  if (!block) return null;

  const elements = block.top_banner_elements?.data;
  if (Array.isArray(elements)) {
    const hasEndDate = elements.some(
      (el) => !!el.attributes?.bannerContent?.endDate
    );

    return { mobile: 72, desktop: hasEndDate ? 48 : 40 };
  }

  if (Array.isArray(block.bannerContent)) {
    const hasEndDate = block.bannerContent.some((bc) => !!bc.endDate);

    return { mobile: 72, desktop: hasEndDate ? 48 : 40 };
  }

  return { mobile: 72, desktop: 40 };
}
